import { useEffect, useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { PATCH_NOTES, type PatchNote } from "../lib/patchNotes";
import { PatchNoteList } from "./PatchNoteList";
import { Sheet } from "./Sheet";

const STORAGE_KEY = "wir_last_seen_patch";

export function PatchNotesOverlay() {
  const { user, initializing } = useAuth();
  const [unseen, setUnseen] = useState<PatchNote[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (initializing || !user || PATCH_NOTES.length === 0) return;
    let lastSeen: string | null = null;
    try {
      lastSeen = localStorage.getItem(STORAGE_KEY);
    } catch {
      return;
    }
    // A device with no stored value is ambiguous: it's either a brand-new
    // account (nothing to catch up on) or an existing user's very first
    // load since this feature itself shipped (who very much has something
    // to catch up on — this is exactly that moment for them). Those can't
    // be told apart from localStorage alone, so fall back to the Firebase
    // account's creation date: an account older than a patch note predates
    // it and should see it; an account created around the same time as the
    // note just joined with that change already baked in, so it isn't
    // "new" to them.
    let baseline = lastSeen;
    if (baseline === null) {
      const createdIso = user.metadata.creationTime ? new Date(user.metadata.creationTime).toISOString().slice(0, 10) : null;
      baseline = createdIso ?? PATCH_NOTES[0].version;
    }
    const newEntries = PATCH_NOTES.filter((n) => n.version > baseline!);
    if (newEntries.length > 0) {
      setUnseen(newEntries);
      setOpen(true);
    } else if (lastSeen === null) {
      try {
        localStorage.setItem(STORAGE_KEY, PATCH_NOTES[0].version);
      } catch {
        // Best-effort — worst case this re-evaluates next load.
      }
    }
  }, [user, initializing]);

  function dismiss() {
    setOpen(false);
    try {
      localStorage.setItem(STORAGE_KEY, PATCH_NOTES[0].version);
    } catch {
      // Best-effort — worst case this shows again next load.
    }
  }

  if (unseen.length === 0) return null;

  return (
    <Sheet open={open} onClose={dismiss} title="Neu in Wir">
      <PatchNoteList notes={unseen} />
      <button onClick={dismiss} className="mt-5 h-11 w-full rounded-xl bg-plum-700 text-sm font-semibold text-cream-50">
        Verstanden
      </button>
    </Sheet>
  );
}
